import { useState, type ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import { AlertDialog, Button, Code, Flex, Text, TextField } from '@radix-ui/themes'
import { Ops } from '@willie/schemas'
import { CopyButton } from '~/components/CopyButton'
import { useTRPC } from '~/utils/trpc'

/**
 * Where the link points. Overridable so a QA link can aim at a dev server on the LAN — a link to
 * production is useless when the app you are testing is running on your laptop.
 */
const INSTALL_APP_BASE_URL = import.meta.env.VITE_INSTALL_APP_URL || Ops.StationInstallations.INSTALL_APP_BASE_URL

interface InstallLinkDialogProps {
  installationId: number
  trigger: ReactNode
}

/**
 * Mints an install link for an operator to open themselves, without emailing the customer.
 *
 * The same site credential the customer holds, at the same version, so opening this is safe on a
 * live installation.
 */
export const InstallLinkDialog = ({ installationId, trigger }: InstallLinkDialogProps) => {
  const trpc = useTRPC()
  const [copied, setCopied] = useState(false)

  const {
    mutate: createLink,
    data,
    isPending,
    error,
    reset
  } = useMutation(trpc.ops.stationInstallations.createInstallLink.mutationOptions())

  const url = data
    ? Ops.StationInstallations.buildCaptureUrl(INSTALL_APP_BASE_URL, data.token, data.installationId)
    : null
  const photosUrl = data ? Ops.StationInstallations.buildMeterPhotosUrl(INSTALL_APP_BASE_URL, data.token) : null

  const copy = async (): Promise<void> => {
    if (!url) return
    // Mirrors the clipboard fallback used elsewhere in this route: writeText rejects when the
    // document is not focused, and ops is often driven from a second monitor.
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <AlertDialog.Root
      onOpenChange={(open) => {
        if (!open) {
          reset()
          setCopied(false)
          return
        }
        createLink({ installationId })
      }}
    >
      <AlertDialog.Trigger>{trigger}</AlertDialog.Trigger>
      <AlertDialog.Content maxWidth="620px">
        <AlertDialog.Title>Install link</AlertDialog.Title>
        <AlertDialog.Description size="2" color="gray">
          The link the customer gets in their guide. Open it on a phone to run the capture flow.
        </AlertDialog.Description>

        <Flex direction="column" gap="3" mt="4">
          {isPending ? (
            <Text size="2" color="gray">
              Minting…
            </Text>
          ) : null}

          {error ? (
            <Text size="2" color="red">
              {error.message}
            </Text>
          ) : null}

          {url ? (
            <>
              <TextField.Root value={url} readOnly onFocus={(event) => event.currentTarget.select()} size="2" />
              <Flex align="center" gap="3">
                <Button variant="soft" onClick={() => void copy()}>
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
                <Text size="1" color="gray">
                  Valid 60 days · points at <Code variant="ghost">{INSTALL_APP_BASE_URL}</Code>
                </Text>
              </Flex>
              {photosUrl ? (
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">
                    Meter photos page
                  </Text>
                  <Flex align="center" gap="2">
                    <TextField.Root
                      value={photosUrl}
                      readOnly
                      onFocus={(event) => event.currentTarget.select()}
                      size="2"
                      className="flex-1"
                    />
                    <CopyButton value={photosUrl} label="Copy meter photos link" iconOnly />
                  </Flex>
                  <Text size="1" color="gray">
                    What the welcome email opens: the customer sends a photo of each meter awaiting one.
                  </Text>
                </Flex>
              ) : null}
              {/* Stated because the opposite would be a reasonable assumption, and getting it wrong
                  would mean an operator avoiding a dialog that costs the customer nothing. */}
              <Text size="1" color="gray">
                Every link for this site is the same credential — showing it invalidates nothing.
              </Text>
            </>
          ) : null}
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <AlertDialog.Cancel>
            <Button variant="soft" color="gray">
              Close
            </Button>
          </AlertDialog.Cancel>
        </Flex>
      </AlertDialog.Content>
    </AlertDialog.Root>
  )
}
